import React, { useState, useEffect } from 'react'
import Container from '@material-ui/core/Container'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import Typography from '@material-ui/core/Typography'
import Switch from '@material-ui/core/Switch'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import LinearProgress from '@material-ui/core/LinearProgress'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Chip from '@material-ui/core/Chip'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import Divider from '@material-ui/core/Divider'
import DeleteIcon from '@material-ui/icons/Delete'
import AddIcon from '@material-ui/icons/Add'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import Loader from 'components/general/Loader'
import SolarReactivationAPI from 'config/api/SolarReactivationAPI'
import { onError } from 'config/lib/errorLib'

const GREEN = '#2dce89'
const RED = '#f5365c'
const INK = '#32325d'

function StatTile({ value, label, help, color }) {
    return (
        <Tooltip title={help || ''} arrow>
            <Box textAlign="center" px={2} style={{ minWidth: 92, cursor: 'help' }}>
                <Typography style={{ fontSize: '1.5rem', fontWeight: 600, color: color || INK }}>{value}</Typography>
                <Typography variant="caption" color="textSecondary" style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</Typography>
            </Box>
        </Tooltip>
    )
}

function SectionTitle({ title, subtitle, help, action }) {
    return (
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} style={{ gap: 12 }}>
            <Box>
                <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <Typography style={{ fontSize: '1.25rem', fontWeight: 600, color: INK }}>{title}</Typography>
                    {help ? (
                        <Tooltip title={help} arrow>
                            <InfoOutlinedIcon fontSize="small" style={{ color: '#adb5bd', cursor: 'help' }} />
                        </Tooltip>
                    ) : null}
                </Box>
                {subtitle ? <Typography color="textSecondary" style={{ fontSize: '0.875rem' }}>{subtitle}</Typography> : null}
            </Box>
            {action ? <Box flexShrink={0}>{action}</Box> : null}
        </Box>
    )
}

function SolarReactivation() {
    const api = new SolarReactivationAPI()

    const [loading, setLoading] = useState(true)
    const [busy, setBusy] = useState(false)
    const [global, setGlobal] = useState({ masterActive: false, businessStart: 9, businessEnd: 18, dailyTarget: 100 })
    const [today, setToday] = useState({ date: '', sent: 0, target: 0 })
    const [settingsForm, setSettingsForm] = useState({ businessStart: 9, businessEnd: 18, dailyTarget: 100 })
    const [variants, setVariants] = useState([])
    const [counties, setCounties] = useState([])

    useEffect(() => {
        loadData()
    }, [])

    async function loadData() {
        setLoading(true)
        try {
            const res = await api.getStatus()
            setGlobal(res.global)
            setToday(res.today || { date: '', sent: 0, target: res.global.dailyTarget })
            setSettingsForm({
                businessStart: res.global.businessStart,
                businessEnd: res.global.businessEnd,
                dailyTarget: res.global.dailyTarget,
            })
            setVariants(res.variants || [])
            setCounties(res.counties || [])
        } catch (e) {
            onError(e)
        }
        setLoading(false)
    }

    async function toggleMaster() {
        const next = !global.masterActive
        setGlobal((g) => ({ ...g, masterActive: next }))
        setBusy(true)
        try {
            await api.setMaster(next)
        } catch (e) {
            onError(e)
            setGlobal((g) => ({ ...g, masterActive: !next }))
        }
        setBusy(false)
    }

    async function toggleCounty(county, active) {
        setBusy(true)
        // optimistic update
        setCounties((prev) => prev.map((c) => (c.county === county ? { ...c, active } : c)))
        try {
            await api.setCounty(county, active)
        } catch (e) {
            onError(e)
            await loadData()
        }
        setBusy(false)
    }

    async function setAll(active) {
        setCounties((prev) => prev.map((c) => ({ ...c, active })))
        setBusy(true)
        try {
            await api.setAll(active)
        } catch (e) {
            onError(e)
            await loadData()
        }
        setBusy(false)
    }

    async function saveSettings() {
        setBusy(true)
        try {
            const payload = {
                businessStart: parseInt(settingsForm.businessStart, 10),
                businessEnd: parseInt(settingsForm.businessEnd, 10),
                dailyTarget: parseInt(settingsForm.dailyTarget, 10),
            }
            await api.setSettings(payload)
            setGlobal((g) => ({ ...g, ...payload }))
        } catch (e) {
            onError(e)
        }
        setBusy(false)
    }

    async function saveVariants(next) {
        const cleaned = next.map((v) => ({
            ...v,
            weight: parseFloat(v.weight) > 0 ? parseFloat(v.weight) : 1,
        }))
        setBusy(true)
        try {
            await api.setSettings({
                messageVariants: cleaned.map((v) => ({ id: v.id, label: v.label, active: v.active, weight: v.weight })),
            })
            setVariants(cleaned)
        } catch (e) {
            onError(e)
        }
        setBusy(false)
    }

    function updateVariant(idx, field, value) {
        setVariants((prev) => prev.map((v, i) => (i === idx ? { ...v, [field]: value } : v)))
    }

    function addVariant() {
        setVariants((prev) => [...prev, { id: `variant-${prev.length + 1}`, label: 'New variant', active: true, weight: 1, sent: 0, positive: 0, negative: 0, conversionRate: 0 }])
    }

    function removeVariant(idx) {
        setVariants((prev) => prev.filter((_, i) => i !== idx))
    }

    if (loading) return <Loader />

    const totals = counties.reduce(
        (acc, c) => ({
            pool: acc.pool + c.poolTotal,
            sent: acc.sent + c.sent,
            positive: acc.positive + c.positive,
            negative: acc.negative + c.negative,
        }),
        { pool: 0, sent: 0, positive: 0, negative: 0 }
    )
    const activeCount = counties.filter((c) => c.active).length

    return (
        <Container maxWidth={false} style={{ paddingTop: '2rem', paddingBottom: '4rem' }}>
            {/* Page header */}
            <Box mb={3}>
                <Typography style={{ fontSize: '1.75rem', fontWeight: 600, color: INK }}>Solar Lead Reactivation</Typography>
                <Typography color="textSecondary" style={{ maxWidth: 780, fontSize: '0.95rem' }}>
                    Re-engage past solar enquiries by SMS. We text people who requested a quote in 2025, hand positive
                    replies straight to a broker, and never contact anyone who opts out.
                </Typography>
            </Box>

            {/* Campaign status */}
            <Card style={{ marginBottom: '1.5rem' }}>
                <CardContent>
                    <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                                <Switch checked={global.masterActive} onChange={toggleMaster} disabled={busy} color="primary" />
                                <Box>
                                    <Typography style={{ fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.2, color: INK }}>
                                        {global.masterActive ? 'Campaign active' : 'Campaign paused'}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {global.masterActive
                                            ? `Texting up to ${global.dailyTarget} leads/day across ${activeCount} active ${activeCount === 1 ? 'county' : 'counties'}.`
                                            : 'No texts are being sent. Turn on to start reactivation.'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Box display="flex" justifyContent="flex-end" alignItems="center" flexWrap="wrap" style={{ gap: 4 }}>
                                <StatTile value={`${today.sent}/${today.target}`} label="Today" help="Texts sent today vs your daily limit. Resets each morning." />
                                <Divider orientation="vertical" flexItem />
                                <StatTile value={totals.pool.toLocaleString()} label="Pool" help="Total eligible 2025 leads across all counties (opt-outs and PVG excluded)." />
                                <Divider orientation="vertical" flexItem />
                                <StatTile value={totals.sent.toLocaleString()} label="Sent" help="Leads texted so far, all time." />
                                <Divider orientation="vertical" flexItem />
                                <StatTile value={totals.positive.toLocaleString()} label="Interested" help="Replied yes — sent to a broker." color={GREEN} />
                                <Divider orientation="vertical" flexItem />
                                <StatTile value={totals.negative.toLocaleString()} label="Opted out" help="Replied no — added to do-not-contact." color={RED} />
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Sending settings */}
            <Card style={{ marginBottom: '1.5rem' }}>
                <CardContent>
                    <SectionTitle
                        title="Sending settings"
                        subtitle="Control how many texts go out and when."
                        help="Texts are spread evenly across your business hours, Monday to Wednesday (Irish time)."
                    />
                    <Grid container spacing={3} alignItems="flex-start">
                        <Grid item xs={12} sm={3}>
                            <TextField label="Leads / day" type="number" variant="outlined" size="small" fullWidth value={settingsForm.dailyTarget}
                                onChange={(e) => setSettingsForm({ ...settingsForm, dailyTarget: e.target.value })}
                                helperText="Max texts per day, spread evenly." />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <TextField label="Start hour" type="number" variant="outlined" size="small" fullWidth value={settingsForm.businessStart}
                                onChange={(e) => setSettingsForm({ ...settingsForm, businessStart: e.target.value })}
                                inputProps={{ min: 0, max: 23 }} helperText="24h, Irish time" />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <TextField label="End hour" type="number" variant="outlined" size="small" fullWidth value={settingsForm.businessEnd}
                                onChange={(e) => setSettingsForm({ ...settingsForm, businessEnd: e.target.value })}
                                inputProps={{ min: 0, max: 23 }} helperText="24h, Irish time" />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Button variant="contained" color="primary" onClick={saveSettings} disabled={busy} style={{ marginTop: 8 }}>Save settings</Button>
                        </Grid>
                    </Grid>
                    <Typography variant="caption" color="textSecondary">
                        Currently up to {global.dailyTarget} texts/day between {global.businessStart}:00 and {global.businessEnd}:00, Mon–Wed (Irish time).
                    </Typography>
                </CardContent>
            </Card>

            {/* Message variant A/B */}
            <Card style={{ marginBottom: '1.5rem' }}>
                <CardContent>
                    <SectionTitle
                        title="Initial message A/B test"
                        subtitle="Test different opening texts to see which gets more replies."
                        help="The bot writes the actual message; each row here is a version it can send. Weights set how often each version is used; Conversion is interested ÷ sent."
                        action={<Button startIcon={<AddIcon />} onClick={addVariant} disabled={busy}>Add version</Button>}
                    />
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Label</TableCell>
                                <TableCell align="center">Active</TableCell>
                                <TableCell align="center">
                                    <Tooltip title="Relative share of sends. Two versions at 0.5 each = a 50/50 split." arrow>
                                        <span style={{ cursor: 'help', borderBottom: '1px dotted #adb5bd' }}>Weight</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="right">Sent</TableCell>
                                <TableCell align="right">Interested</TableCell>
                                <TableCell align="right">Opted out</TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Interested replies ÷ texts sent." arrow>
                                        <span style={{ cursor: 'help', borderBottom: '1px dotted #adb5bd' }}>Conversion</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {variants.map((v, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>
                                        <TextField value={v.id} variant="outlined" size="small" onChange={(e) => updateVariant(idx, 'id', e.target.value)} />
                                    </TableCell>
                                    <TableCell>
                                        <TextField value={v.label} variant="outlined" size="small" onChange={(e) => updateVariant(idx, 'label', e.target.value)} />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Switch checked={v.active !== false} color="primary"
                                            onChange={(e) => updateVariant(idx, 'active', e.target.checked)} />
                                    </TableCell>
                                    <TableCell align="center" style={{ width: 90 }}>
                                        <TextField type="number" value={v.weight} variant="outlined" size="small"
                                            inputProps={{ step: 0.5, min: 0 }}
                                            onChange={(e) => updateVariant(idx, 'weight', e.target.value)} />
                                    </TableCell>
                                    <TableCell align="right">{v.sent}</TableCell>
                                    <TableCell align="right" style={{ color: GREEN }}>{v.positive}</TableCell>
                                    <TableCell align="right" style={{ color: RED }}>{v.negative}</TableCell>
                                    <TableCell align="right" style={{ fontWeight: 600 }}>{(v.conversionRate * 100).toFixed(1)}%</TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Remove version" arrow>
                                            <IconButton size="small" onClick={() => removeVariant(idx)}><DeleteIcon fontSize="small" /></IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <Box mt={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 12 }}>
                        <Typography variant="caption" color="textSecondary">
                            The <b>ID</b> must match a version the bot knows (e.g. <code>county</code>, <code>locality</code>). The bot owns the wording.
                        </Typography>
                        <Button variant="contained" color="primary" onClick={() => saveVariants(variants)} disabled={busy}>Save versions</Button>
                    </Box>
                </CardContent>
            </Card>

            {/* County controls */}
            <Card>
                <CardContent>
                    <SectionTitle
                        title="Counties"
                        subtitle="Turn reactivation on for the counties you need leads in. Only active counties are texted."
                        help="Each card shows how far through that county's pool you are: texted vs total, with how many are still available. Green = interested, red = opted out."
                        action={
                            <Box style={{ gap: 8, display: 'flex' }}>
                                <Button size="small" variant="outlined" onClick={() => setAll(true)} disabled={busy}>All on</Button>
                                <Button size="small" variant="outlined" onClick={() => setAll(false)} disabled={busy}>All off</Button>
                            </Box>
                        }
                    />
                    <Grid container spacing={2}>
                        {counties.map((c) => {
                            const pct = c.poolTotal > 0 ? Math.min(100, (c.sent / c.poolTotal) * 100) : 0
                            return (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={c.county}>
                                    <Card variant="outlined" style={{ opacity: c.active ? 1 : 0.55 }}>
                                        <CardContent>
                                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                                <Box>
                                                    <Typography style={{ fontSize: '1rem', fontWeight: 600, lineHeight: 1.1, color: INK }}>{c.county}</Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {c.available.toLocaleString()} of {c.poolTotal.toLocaleString()} left
                                                    </Typography>
                                                </Box>
                                                <Tooltip title={c.active ? 'Texting active — click to pause' : 'Paused — click to start texting this county'} arrow>
                                                    <Switch checked={c.active} color="primary" disabled={busy}
                                                        onChange={(e) => toggleCounty(c.county, e.target.checked)} />
                                                </Tooltip>
                                            </Box>
                                            <Tooltip title={`${c.sent.toLocaleString()} of ${c.poolTotal.toLocaleString()} texted (${pct.toFixed(0)}%)`} arrow>
                                                <LinearProgress variant="determinate" value={pct} style={{ height: 8, borderRadius: 4, margin: '10px 0' }} />
                                            </Tooltip>
                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Typography variant="caption" color="textSecondary">
                                                    {c.sent.toLocaleString()} texted
                                                </Typography>
                                                <Box display="flex" style={{ gap: 6 }}>
                                                    <Tooltip title="Interested (sent to a broker)" arrow>
                                                        <Chip size="small" label={`+${c.positive}`} style={{ backgroundColor: GREEN, color: 'white', height: 20 }} />
                                                    </Tooltip>
                                                    <Tooltip title="Opted out (do-not-contact)" arrow>
                                                        <Chip size="small" label={`-${c.negative}`} style={{ backgroundColor: RED, color: 'white', height: 20 }} />
                                                    </Tooltip>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )
                        })}
                    </Grid>
                </CardContent>
            </Card>
        </Container>
    )
}

export default SolarReactivation
